/**
 * P8-P0 — Icon Registry (FROZEN inventory + naming convention).  [expanded in PR2]
 *
 * Single source of truth mapping a stable registry name → a concrete icon
 * component. ALL app icons resolve through here so icons can later be replaced
 * centrally (admin/runtime overrides, P8-P1/P2), directional icons can flip in
 * RTL via `rtlFlip` metadata (P9; NOT activated), and web/Flutter share one
 * semantic key space.
 *
 * ── Naming convention (FROZEN) ──────────────────────────────────────────────
 *  1. Keys are lowercase kebab-case.
 *  2. PASSTHROUGH keys = kebab-case of the lucide export (`Trash2`→`trash-2`).
 *     Every icon used in the app has a deterministic passthrough key — the
 *     migration codemod's 1:1 target.
 *  3. SEMANTIC keys — two forms (both alias an existing passthrough entry):
 *       a) flat role names: `spinner`, `close`, `success` …
 *       b) NAMESPACED `group.role` names: `navigation.dashboard`, `billing.invoice`,
 *          `profile.user`, `admin.users` … (preferred for nav/domain UI).
 *     New code SHOULD prefer semantic keys; migrations MAY use passthrough.
 *  4. `rtlFlip: true` = horizontally-directional icon (metadata only; not active).
 *  5. Unknown name → neutral `circle` fallback + dev warning (never blank/throws).
 *
 * NOTE: full distinct lucide inventory across the app = 188 icons (PR1 doc said
 * ~98; that undercount was a single-line-import regex artefact — corrected here).
 */
import {
  Activity, AlertCircle, AlertOctagon, AlertTriangle, Apple, Archive, ArrowDown,
  ArrowDownRight, ArrowLeft, ArrowRight, ArrowUp, ArrowUpDown, ArrowUpRight, AtSign,
  BadgeCheck, Ban, BarChart3, Bell, BellOff, Bike, Blocks, BookOpen, Bookmark, BookmarkCheck,
  Bot, Boxes, Brain, Building, Building2, Calculator, Calendar, CalendarClock, Camera, Car,
  CarFront, Check, CheckCheck, CheckCircle, CheckCircle2, CheckSquare, ChevronDown,
  ChevronLeft, ChevronRight, ChevronUp, Circle, CircleDot, CircleHelp, ClipboardCheck,
  ClipboardList, Clock, Cloud, Code, Code2, Cog, Copy, CreditCard, Crosshair, Database,
  DollarSign, DoorOpen, Download, Edit, Edit2, Edit3, ExternalLink, Eye, EyeOff, Facebook,
  FileMinus, FileSpreadsheet, FileText, Filter, Fingerprint, FlaskConical, Frown, Fuel, Gauge,
  Globe, HardDrive, Hash, HelpCircle, History, Home, Image, ImageOff, ImagePlus, Inbox, Info,
  Instagram, Key, KeyRound, Languages, Layers, LayoutDashboard, LifeBuoy, Lightbulb, Link2,
  Linkedin, List, ListChecks, ListOrdered, Loader2, Lock, LogIn, LogOut, Mail, MailOpen, Map,
  MapPin, Menu, MessageSquare, Minus, Monitor, MoreHorizontal, MoreVertical, Navigation,
  Package, PackageSearch, Palette, Paperclip, Pause, PenLine, Pencil, Phone, PhoneCall,
  PhoneMissed, PhoneOff, PieChart, Play, Plug, Plus, Power, Quote, Receipt, RefreshCcw,
  RefreshCw, Repeat, RotateCcw, Ruler, Save, ScanLine, Search, SearchCheck, SearchX, Send,
  Settings, Settings2, Shield, ShieldAlert, ShieldCheck, ShieldOff, ShoppingCart, Sliders,
  SlidersHorizontal, Smartphone, Sparkles, Square, Star, StarHalf, StickyNote, Store, Tag,
  ThumbsDown, Ticket, Timer, ToggleLeft, Trash, Trash2, TrendingUp, Trophy, Truck, Twitter, Type,
  Undo2, Upload, User, UserCheck, UserPlus, UserX, Users, Voicemail, Wallet, Wand2, Wifi,
  WifiOff, Wrench, X, XCircle, Zap,
} from 'lucide-react';

// kebab-case passthrough name → lucide component (every icon used in the app)
const RAW = {
  'activity': Activity,
  'alert-circle': AlertCircle,
  'alert-octagon': AlertOctagon,
  'alert-triangle': AlertTriangle,
  'apple': Apple,
  'archive': Archive,
  'arrow-down': ArrowDown,
  'arrow-down-right': ArrowDownRight,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-up-down': ArrowUpDown,
  'arrow-up-right': ArrowUpRight,
  'at-sign': AtSign,
  'badge-check': BadgeCheck,
  'ban': Ban,
  'bar-chart-3': BarChart3,
  'bell': Bell,
  'bell-off': BellOff,
  'bike': Bike,
  'blocks': Blocks,
  'book-open': BookOpen,
  'bookmark': Bookmark,
  'bookmark-check': BookmarkCheck,
  'bot': Bot,
  'boxes': Boxes,
  'brain': Brain,
  'building': Building,
  'building-2': Building2,
  'calculator': Calculator,
  'calendar': Calendar,
  'calendar-clock': CalendarClock,
  'camera': Camera,
  'car': Car,
  'car-front': CarFront,
  'check': Check,
  'check-check': CheckCheck,
  'check-circle': CheckCircle,
  'check-circle-2': CheckCircle2,
  'check-square': CheckSquare,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'circle': Circle,
  'circle-dot': CircleDot,
  'circle-help': CircleHelp,
  'clipboard-check': ClipboardCheck,
  'clipboard-list': ClipboardList,
  'clock': Clock,
  'cloud': Cloud,
  'code': Code,
  'code-2': Code2,
  'cog': Cog,
  'copy': Copy,
  'credit-card': CreditCard,
  'crosshair': Crosshair,
  'database': Database,
  'dollar-sign': DollarSign,
  'door-open': DoorOpen,
  'download': Download,
  'edit': Edit,
  'edit-2': Edit2,
  'edit-3': Edit3,
  'external-link': ExternalLink,
  'eye': Eye,
  'eye-off': EyeOff,
  'facebook': Facebook,
  'file-minus': FileMinus,
  'file-spreadsheet': FileSpreadsheet,
  'file-text': FileText,
  'filter': Filter,
  'fingerprint': Fingerprint,
  'flask-conical': FlaskConical,
  'frown': Frown,
  'fuel': Fuel,
  'gauge': Gauge,
  'globe': Globe,
  'hard-drive': HardDrive,
  'hash': Hash,
  'help-circle': HelpCircle,
  'history': History,
  'home': Home,
  'image': Image,
  'image-off': ImageOff,
  'image-plus': ImagePlus,
  'inbox': Inbox,
  'info': Info,
  'instagram': Instagram,
  'key': Key,
  'key-round': KeyRound,
  'languages': Languages,
  'layers': Layers,
  'layout-dashboard': LayoutDashboard,
  'life-buoy': LifeBuoy,
  'lightbulb': Lightbulb,
  'link-2': Link2,
  'linkedin': Linkedin,
  'list': List,
  'list-checks': ListChecks,
  'list-ordered': ListOrdered,
  'loader-2': Loader2,
  'lock': Lock,
  'log-in': LogIn,
  'log-out': LogOut,
  'mail': Mail,
  'mail-open': MailOpen,
  'map': Map,
  'map-pin': MapPin,
  'menu': Menu,
  'message-square': MessageSquare,
  'minus': Minus,
  'monitor': Monitor,
  'more-horizontal': MoreHorizontal,
  'more-vertical': MoreVertical,
  'navigation': Navigation,
  'package': Package,
  'package-search': PackageSearch,
  'palette': Palette,
  'paperclip': Paperclip,
  'pause': Pause,
  'pen-line': PenLine,
  'pencil': Pencil,
  'phone': Phone,
  'phone-call': PhoneCall,
  'phone-missed': PhoneMissed,
  'phone-off': PhoneOff,
  'pie-chart': PieChart,
  'play': Play,
  'plug': Plug,
  'plus': Plus,
  'power': Power,
  'quote': Quote,
  'receipt': Receipt,
  'refresh-ccw': RefreshCcw,
  'refresh-cw': RefreshCw,
  'repeat': Repeat,
  'rotate-ccw': RotateCcw,
  'ruler': Ruler,
  'save': Save,
  'scan-line': ScanLine,
  'search': Search,
  'search-check': SearchCheck,
  'search-x': SearchX,
  'send': Send,
  'settings': Settings,
  'settings-2': Settings2,
  'shield': Shield,
  'shield-alert': ShieldAlert,
  'shield-check': ShieldCheck,
  'shield-off': ShieldOff,
  'shopping-cart': ShoppingCart,
  'sliders': Sliders,
  'sliders-horizontal': SlidersHorizontal,
  'smartphone': Smartphone,
  'sparkles': Sparkles,
  'square': Square,
  'star': Star,
  'star-half': StarHalf,
  'sticky-note': StickyNote,
  'store': Store,
  'tag': Tag,
  'thumbs-down': ThumbsDown,
  'ticket': Ticket,
  'timer': Timer,
  'toggle-left': ToggleLeft,
  'trash': Trash,
  'trash-2': Trash2,
  'trending-up': TrendingUp,
  'trophy': Trophy,
  'truck': Truck,
  'twitter': Twitter,
  'type': Type,
  'undo-2': Undo2,
  'upload': Upload,
  'user': User,
  'user-check': UserCheck,
  'user-plus': UserPlus,
  'user-x': UserX,
  'users': Users,
  'voicemail': Voicemail,
  'wallet': Wallet,
  'wand-2': Wand2,
  'wifi': Wifi,
  'wifi-off': WifiOff,
  'wrench': Wrench,
  'x': X,
  'x-circle': XCircle,
  'zap': Zap,
};

// Curated semantic aliases (flat + namespaced) → existing passthrough key.
const ALIASES = {
  'spinner': 'loader-2',
  'close': 'x',
  'success': 'check-circle',
  'error': 'x-circle',
  'warning': 'alert-triangle',
  'add': 'plus',
  'edit': 'pencil',
  'delete': 'trash-2',
  'back': 'arrow-left',
  'forward': 'arrow-right',
  'refresh': 'refresh-cw',
  'dashboard': 'layout-dashboard',
  'vehicle': 'car',
  'repair': 'wrench',
  'part': 'package',
  'rfq': 'clipboard-list',
  'vehicle-care': 'shield-check',
  'billing': 'receipt',
  'notification': 'bell',
  'support': 'life-buoy',
  'profile': 'user',
  'admin': 'shield',
  'navigation.dashboard': 'layout-dashboard',
  'navigation.menu': 'menu',
  'navigation.close': 'x',
  'navigation.back': 'arrow-left',
  'navigation.forward': 'arrow-right',
  'navigation.next': 'chevron-right',
  'navigation.prev': 'chevron-left',
  'navigation.expand': 'chevron-down',
  'navigation.collapse': 'chevron-up',
  'navigation.search': 'search',
  'navigation.settings': 'settings',
  'navigation.logout': 'log-out',
  'navigation.external': 'external-link',
  'navigation.home': 'home',
  'vehicles.car': 'car',
  'vehicles.car-front': 'car-front',
  'vehicles.truck': 'truck',
  'vehicles.bike': 'bike',
  'vehicles.fuel': 'fuel',
  'vehicles.gauge': 'gauge',
  'vehicles.vin': 'scan-line',
  'billing.invoice': 'receipt',
  'billing.payment': 'credit-card',
  'billing.plan': 'dollar-sign',
  'billing.wallet': 'wallet',
  'billing.cart': 'shopping-cart',
  'billing.usage': 'bar-chart-3',
  'notifications.bell': 'bell',
  'notifications.bell-off': 'bell-off',
  'notifications.alert': 'alert-triangle',
  'notifications.info': 'info',
  'support.help': 'life-buoy',
  'support.ticket': 'ticket',
  'support.message': 'message-square',
  'support.phone': 'phone',
  'support.mail': 'mail',
  'profile.user': 'user',
  'profile.user-add': 'user-plus',
  'profile.user-check': 'user-check',
  'profile.identity': 'fingerprint',
  'admin.shield': 'shield',
  'admin.verified': 'shield-check',
  'admin.alert': 'shield-alert',
  'admin.users': 'users',
  'admin.database': 'database',
  'admin.settings': 'cog',
  'admin.integrations': 'plug',
  'admin.activity': 'activity',
  'admin.content': 'file-text',
};

// Horizontally-directional icons that mirror in RTL (metadata only; not active).
const RTL_FLIP = new Set(['arrow-left', 'arrow-right', 'arrow-up-right', 'arrow-down-right', 'chevron-left', 'chevron-right', 'send', 'log-in', 'log-out', 'external-link', 'play', 'undo-2']);

const ICON_REGISTRY = {};
for (const [name, Comp] of Object.entries(RAW)) {
  ICON_REGISTRY[name] = { Comp, rtlFlip: RTL_FLIP.has(name) };
}
for (const [alias, target] of Object.entries(ALIASES)) {
  if (ICON_REGISTRY[target]) ICON_REGISTRY[alias] = ICON_REGISTRY[target];
}

/** Fallback used when a name is not registered. Never renders blank. */
export const FALLBACK_ICON = Circle;

/** Lookup a registry entry by name. Returns undefined if unknown. */
export function getIconEntry(name) {
  return ICON_REGISTRY[name];
}

/** Sorted list of all registered names (passthrough + semantic). For CI parity. */
export function listIconNames() {
  return Object.keys(ICON_REGISTRY).sort();
}

export default ICON_REGISTRY;
